'use client';

import { c as _c } from "react/compiler-runtime";
import type { GetStudentMentoringStateOutputDTO } from '@algoacademy/backend/modules/payment/endpoints/queries/get-student-mentoring-state/output.dto';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { DescriptionDetails } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionDetails';
import { DescriptionList } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionList';
import { DescriptionTerm } from '@algoacademy/design-system/web/data-display/DescriptionList/DescriptionTerm';
import { formatWarsawLongDate } from '@algoacademy/shared/warsaw-calendar';
import { formatGrosze, formatTermsSummary } from '../_helpers/mentoring-terms';

/**
 * The CRM has no address for a single contact - contacts open in a drawer over
 * the list - so staff lands on the list and searches the buyer's e-mail, which
 * the panel prints right next to the link.
 */
const CRM_CONTACTS_HREF = '/crm/contacts';
type StudentSubscriptionPanelProps = {
  subscriptionState: GetStudentMentoringStateOutputDTO['subscriptionState'];
  current: NonNullable<GetStudentMentoringStateOutputDTO['current']>;
  scheduledChange: GetStudentMentoringStateOutputDTO['scheduledChange'];
};

/**
 * What this student is on right now, in the words the backend already chose:
 * mentoring, cadence, hours, who pays, when it renews - plus the change waiting
 * for that renewal, when one waits. Read-only; every state renders it, because
 * staff needs the same facts whether or not a change is possible.
 */
export function StudentSubscriptionPanel(t0) {
  const $ = _c(36);
  const {
    subscriptionState,
    current,
    scheduledChange
  } = t0;
  const renewalLabel = subscriptionState === "cancelled" ? "Ko\u0144czy si\u0119" : "Odnawia si\u0119";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="font-medium text-contentPrimary text-sm">Aktualna subskrypcja ucznia</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DescriptionTerm>Mentoring</DescriptionTerm>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== current.billingCadenceLabel || $[3] !== current.hoursWithMentor || $[4] !== current.mentoringTypeLabel) {
    t3 = formatTermsSummary({
      mentoringTypeLabel: current.mentoringTypeLabel,
      billingCadenceLabel: current.billingCadenceLabel,
      hoursWithMentor: current.hoursWithMentor
    });
    $[2] = current.billingCadenceLabel;
    $[3] = current.hoursWithMentor;
    $[4] = current.mentoringTypeLabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <DescriptionDetails>{t3}</DescriptionDetails>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DescriptionTerm>Uczeń</DescriptionTerm>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== current.studentName) {
    t6 = <DescriptionDetails>{current.studentName}</DescriptionDetails>;
    $[9] = current.studentName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <DescriptionTerm>Kupujący</DescriptionTerm>;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== current.buyerEmail || $[13] !== current.buyerName) {
    t8 = <DescriptionDetails>{current.buyerName} ({current.buyerEmail})</DescriptionDetails>;
    $[12] = current.buyerEmail;
    $[13] = current.buyerName;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== renewalLabel) {
    t9 = <DescriptionTerm>{renewalLabel}</DescriptionTerm>;
    $[15] = renewalLabel;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== current.renewsAt) {
    t10 = formatWarsawLongDate(current.renewsAt);
    $[17] = current.renewsAt;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10) {
    t11 = <DescriptionDetails>{t10}</DescriptionDetails>;
    $[19] = t10;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t4 || $[23] !== t6 || $[24] !== t8 || $[25] !== t9) {
    t12 = <DescriptionList>{t2}{t4}{t5}{t6}{t7}{t8}{t9}{t11}</DescriptionList>;
    $[21] = t11;
    $[22] = t4;
    $[23] = t6;
    $[24] = t8;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== scheduledChange) {
    t13 = scheduledChange === null ? null : <div className="flex flex-col gap-1 rounded-lg border border-borderPrimary bg-backgroundPrimary p-3"><p className="font-medium text-contentPrimary text-sm">Zaplanowana zmiana</p><p className="text-contentSecondary text-sm">Od {formatWarsawLongDate(scheduledChange.effectiveAt)} mentoring zmienia się na:{" "}{formatTermsSummary({
          mentoringTypeLabel: scheduledChange.mentoringTypeLabel,
          billingCadenceLabel: scheduledChange.billingCadenceLabel,
          hoursWithMentor: scheduledChange.hoursWithMentor
        })}. Cena odnowienia: {formatGrosze(scheduledChange.priceGrosze)} - w cenie 23% VAT. Do tej daty nic się nie zmienia.</p></div>;
    $[27] = scheduledChange;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Link href={CRM_CONTACTS_HREF} target="_blank" rel="noreferrer" className="text-algoprimary underline">otwórz listę kontaktów</Link>;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== current.buyerEmail) {
    t15 = <p className="text-contentSecondary text-sm">Historia zmian jest na kontakcie kupującego w CRM -{" "}{t14}{" "}i wyszukaj {current.buyerEmail}.</p>;
    $[30] = current.buyerEmail;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t12 || $[33] !== t13 || $[34] !== t15) {
    t16 = <div className="flex flex-col gap-3 rounded-lg border border-borderPrimary bg-backgroundSecondary p-4">{t1}{t12}{t13}{t15}</div>;
    $[32] = t12;
    $[33] = t13;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}